import { ToastPrimitive } from '@/components/CofheFloatingButton/components/ToastPrimitives';
import type { CofheFloatingButtonToast, CofheToastImperativeParams } from '@/components/CofheFloatingButton/types';
import { isReactNode } from '@/utils';
import { create } from 'zustand';

type PortalToastsStore = {
  toasts: CofheFloatingButtonToast[];
};

type PortalToastsActions = {
  addToast: (toast: React.ReactNode | CofheToastImperativeParams, duration?: number | 'infinite') => void;
  pauseToast: (id: string, paused: boolean) => void;
  removeToast: (id: string) => void;
};

export const usePortalToasts = create<PortalToastsStore & PortalToastsActions>()((set, get) => ({
  toasts: [],

  addToast: (toast, duration = 5000) => {
    const content = isReactNode(toast) ? toast : <ToastPrimitive {...toast} />;
    const existing = get().toasts;
    const updated = [
      ...existing,
      {
        id: crypto.randomUUID(),
        duration,
        startMs: Date.now(),
        remainingMs: duration === 'infinite' ? Infinity : duration,
        paused: false,
        content,
      },
    ];
    set({ toasts: updated });
  },
  pauseToast: (id, paused) => {
    const existing = get().toasts;
    const updated = existing.map((t) => {
      if (t.id !== id) return t;
      if (t.paused === paused) return t;

      let remainingMs = t.remainingMs;
      let startMs = Date.now();
      if (paused) {
        const elapsedMs = Date.now() - t.startMs;
        remainingMs = Math.max(0, t.remainingMs - elapsedMs);
      }

      return { ...t, paused, startMs, remainingMs };
    });
    set({ toasts: updated });
  },
  removeToast: (id) => {
    const existing = get().toasts;
    set({ toasts: existing.filter((t) => t.id !== id) });
  },
}));
